import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import { lineReveal, easeOut } from "@/lib/motion";
import { useEarlyAccess } from "./EarlyAccessContext";

const headline = [
  ["The", "Network", "Where"],
  ["Real", "Estate", "Grows."],
];

const HERO_IMG =
  "https://customer-assets-jai6qajn.emergentagent.net/job_lintel-early/artifacts/933j1shl_ChatGPT%20Image%20Aug%203%2C%202026%2C%2004_04_07%20PM-Photoroom.png";

export default function Hero() {
  const { open } = useEarlyAccess();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0px", "-30px"]);

  return (
    <section
      id="top"
      ref={ref}
      className="grid-lines relative flex min-h-[70vh] flex-col items-center overflow-hidden bg-[#f5f7fb] pb-12 pt-16 text-center sm:pt-20"
      data-testid="hero-section"
    >
      {/* radial glows */}
      <div className="pointer-events-none absolute left-1/2 top-12 h-[340px] w-[500px] -translate-x-1/2 rounded-full bg-[#3b82f6]/15 blur-[130px]" />
      <div className="pointer-events-none absolute -left-24 top-1/3 h-[220px] w-[220px] rounded-full bg-[#1e3a8a]/10 blur-[120px]" />

      <div className="relative z-10 mx-auto flex w-full max-w-4xl flex-col items-center px-2.5 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: easeOut }}
          className="mb-4 mt-6 inline-flex items-center gap-2.5 rounded-full border border-[#dbe3f0] bg-white/70 py-1 pl-1.5 pr-3 backdrop-blur sm:mt-10"
        >
          <span className="rounded-full bg-[#1e3a8a] px-2.5 py-0.5 text-[11px] font-semibold text-white">
            New
          </span>
          <span className="eyebrow text-[#1e3a8a]">Now onboarding — Early Access</span>
        </motion.div>

        <h1 className="text-[1.7rem] font-extrabold leading-[1] tracking-tight text-[#111827] sm:text-4xl lg:text-[3.2rem]">
          {headline.map((line, li) => (
            <span key={li} className="line-mask">
              <motion.span
                className="inline-block"
                variants={lineReveal}
                initial="hidden"
                animate="show"
                transition={{ delay: 0.3 + li * 0.14 }}
              >
                {line.map((w, wi) => (
                  <span
                    key={wi}
                    className={
                      w === "Grows." || w === "Network"
                        ? "text-[#1e3a8a]"
                        : undefined
                    }
                  >
                    {w}
                    {wi < line.length - 1 ? "\u00A0" : ""}
                  </span>
                ))}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease: easeOut }}
          className="mt-3.5 max-w-lg text-sm leading-relaxed text-[#475569] sm:text-base"
        >
          Connecting builders and channel partners to new opportunities,
          stronger partnerships, and more growth.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: easeOut }}
          className="mt-5 flex w-full max-w-[320px] flex-col gap-3 px-2.5 sm:max-w-none sm:flex-row sm:items-center sm:justify-center sm:px-0"
        >
          <button
            onClick={() => open("general")}
            className="btn-primary group flex items-center justify-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold text-white transition-all sm:px-5 sm:py-2 sm:text-[13px] sm:w-auto"
            data-testid="hero-join-button"
          >
            Join Early Access
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
          <a
            href="#manifesto"
            className="flex items-center justify-center gap-2 rounded-full border border-[#d5dced] bg-white px-4 py-1.5 text-xs font-semibold text-[#1e3a8a] shadow-[0_10px_30px_-18px_rgba(30,58,138,0.4)] transition-all hover:border-[#1e3a8a]/40 hover:bg-[#f5f7fb] sm:px-5 sm:py-2 sm:text-[13px] sm:w-auto"
            data-testid="hero-learn-button"
          >
            <MessageCircle className="h-4 w-4" />
            Why LintelGrid
          </a>
        </motion.div>
      </div>

      {/* Centered platform dashboard */}
      <motion.div
        initial={{ opacity: 0, y: 60, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 1.1, delay: 0.55, ease: easeOut }}
        style={{ y: imgY }}
        className="relative z-10 mt-8 w-full max-w-2xl px-2.5 sm:px-6"
      >
        <div className="pointer-events-none absolute left-1/2 top-8 -z-10 h-3/4 w-3/4 -translate-x-1/2 rounded-[60px] bg-gradient-to-tr from-[#1e3a8a]/20 via-[#3b82f6]/20 to-transparent blur-[80px]" />
        <motion.img
          src={HERO_IMG}
          alt="LintelGrid platform dashboard"
          animate={{ y: [0, -12, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="mx-auto w-full drop-shadow-[0_50px_90px_rgba(15,23,42,0.28)]"
        />

        {/* floating status chip */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.15, duration: 0.6, ease: easeOut }}
          className="absolute left-2 top-10 hidden scale-90 items-center gap-2 rounded-xl border border-[#e5e7eb] bg-white/90 px-3.5 py-2.5 shadow-[0_15px_40px_-15px_rgba(30,58,138,0.4)] backdrop-blur sm:left-6 sm:flex md:-left-8"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#3b82f6]/10">
            <span className="h-2.5 w-2.5 rounded-full bg-[#22c55e]" />
          </span>
          <div className="text-left">
            <p className="font-mono-plex text-[10px] uppercase tracking-widest text-[#94a3b8]">
              Live pipeline
            </p>
            <p className="text-sm font-bold text-[#111827]">1,248 leads</p>
          </div>
        </motion.div>

        {/* floating conversion chip */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.3, duration: 0.6, ease: easeOut }}
          className="absolute right-2 top-20 hidden scale-90 items-center gap-2 rounded-xl border border-[#e5e7eb] bg-white/90 px-3.5 py-2.5 shadow-[0_15px_40px_-15px_rgba(30,58,138,0.4)] backdrop-blur sm:right-6 sm:flex md:-right-8"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#1e3a8a]/10 text-[#1e3a8a]">
            <ArrowRight className="h-4 w-4 -rotate-45" />
          </span>
          <div className="text-left">
            <p className="font-mono-plex text-[10px] uppercase tracking-widest text-[#94a3b8]">
              Conversion
            </p>
            <p className="text-sm font-bold text-[#111827]">+18.5%</p>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
